import '@/assets/styles/tokens.css'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import AppModal from './components/AppModal.vue'
import router from './router'
import { reportClientError, isOneSignalSdkNoise } from './services/errorReportService'
import { setupNotificationHandlers } from './services/notificationService'
import { setupWebPushClickHandling } from './services/webPushService'

const app = createApp(App)
app.use(createPinia())
app.use(router)
app.component('AppModal', AppModal)

// Detecta falha ao carregar um chunk JS (ChunkLoadError, import dinâmico
// rejeitado, ou o "Unexpected token '<'" clássico de um chunk que sumiu
// depois de um deploy e o servidor devolveu HTML no lugar do .js). Nesse
// caso a aba está com uma versão desatualizada do app — recarregar a
// página busca o index.html/chunks atuais e resolve sozinho. Só recarrega
// uma vez (sessionStorage) pra não entrar em loop se o problema persistir.
const RELOAD_FLAG = 'chunk-reload-attempted'
const CHUNK_ERROR_PATTERN = /loading chunk|failed to fetch dynamically imported module|chunkloaderror|unexpected token '<'/i

function recoverFromChunkLoadFailure(message) {
  if (!CHUNK_ERROR_PATTERN.test(message ?? '')) return false
  if (sessionStorage.getItem(RELOAD_FLAG)) return false
  sessionStorage.setItem(RELOAD_FLAG, '1')
  window.location.reload()
  return true
}

// Captura qualquer erro não tratado (componentes Vue, promises, scripts) e
// manda para os Logs de Erro do backend — a UI segue mostrando só o toast
// genérico já existente em cada tela, mas o erro completo fica registrado.
app.config.errorHandler = (err, instance, info) => {
  if (recoverFromChunkLoadFailure(err?.message)) return
  if (isOneSignalSdkNoise(err?.stack)) { console.error(err); return }
  reportClientError({
    message: err?.message ?? String(err),
    stack:   err?.stack,
    context: `vue.errorHandler (${info})`,
    details: { component: instance?.$options?.name ?? instance?.$options?.__name },
  })
  console.error(err)
}

router.onError((error) => {
  if (recoverFromChunkLoadFailure(error?.message)) return
  if (isOneSignalSdkNoise(error?.stack)) return
  reportClientError({
    message: error?.message ?? String(error),
    stack:   error?.stack,
    context: 'router.onError',
  })
})

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  if (recoverFromChunkLoadFailure(reason?.message)) return
  if (isOneSignalSdkNoise(reason?.stack)) return
  reportClientError({
    message: reason?.message ?? String(reason),
    stack:   reason?.stack,
    context: 'window.unhandledrejection',
  })
})

window.addEventListener('error', (event) => {
  if (recoverFromChunkLoadFailure(event.message)) return
  if (isOneSignalSdkNoise(event.error?.stack)) return
  reportClientError({
    message: event.message,
    stack:   event.error?.stack,
    context: 'window.onerror',
    details: { filename: event.filename, lineno: event.lineno, colno: event.colno },
  })
})

// Initialize OneSignal Web push (no-op when key is absent or SDK throws).
// Wrapped in a self-contained async block so it never delays app mount.
const appId = process.env.VUE_APP_ONESIGNAL_APP_ID || ''
if (appId) {
  ;(async () => {
    try {
      const { default: OneSignalVue, useOneSignal } = await import('@onesignal/onesignal-vue3')
      app.use(OneSignalVue, { appId })
      // Anexa os listeners (foreground display + mudança de subscrição) uma
      // única vez -- sem isso, registerAdmin/notificationService nunca sabem
      // que o SDK está pronto e o pipeline de push web fica todo inativo.
      setupNotificationHandlers(useOneSignal(), router)
    } catch {
      // OneSignal unavailable — continue without push notifications
    }
  })()
}

// Clique em push web via FCM (aba aberta: postMessage do service worker; aba fechada: ?pushClick= na URL).
// Independente do OneSignal acima: só age quando o painel está no canal FCM (ADR-0002).
setupWebPushClickHandling(router)

app.mount('#app')

// Chegou até aqui rodando o bundle atual com sucesso — libera a trava de
// reload pra um eventual próximo deploy (dias depois, mesma aba) também
// poder se autorrecuperar, em vez de ficar preso à tentativa antiga.
sessionStorage.removeItem(RELOAD_FLAG)
